// frontend/src/clientLog.js
//
// 前端診斷日誌：把使用者這一次操作的關鍵事件（選檔、上傳、WebSocket 狀態、錯誤）
// 批次送到後端 /client-log/。目的是補上伺服器端看不到的那一段——例如
// 「Android 按下上傳馬上 Network Error」，請求根本沒抵達伺服器，只有瀏覽器端知道發生什麼事。
//
// 設計原則：
//  - 絕不影響主流程：任何失敗都吞掉，送不出去就算了
//  - 省流量：2 秒內的事件合併成一批送；error 等級立即送
//  - 離開頁面／切到背景時用 sendBeacon 把剩下的送出（fetch 在頁面卸載時會被取消）
//  - 診斷碼（sid）存在 localStorage，使用者回報問題時唸出來就能撈到整段紀錄

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const ENDPOINT = `${API_BASE}/client-log/`;
const FLUSH_DELAY_MS = 2000;
const MAX_BUFFER = 200;

const makeId = () => Math.random().toString(36).slice(2, 8);

const sid = (() => {
  try {
    const key = 'whisper_sid';
    let s = localStorage.getItem(key);
    if (!s || s.length !== 6) {
      s = makeId();
      localStorage.setItem(key, s);
    }
    return s;
  } catch (e) {
    return makeId();   // 無痕模式或封鎖儲存：這次先用臨時的
  }
})();
const pageId = makeId();   // 每次載入頁面不同，區分同一個人的多次開啟

let buffer = [];
let uploadId = null;
let timer = null;
let seq = 0;

function context() {
  const nav = navigator;
  const conn = nav.connection || {};
  return {
    ua: nav.userAgent,
    lang: nav.language,
    online: nav.onLine,
    net: conn.effectiveType || null,
    screen: `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    url: window.location.href,
    visible: document.visibilityState,
  };
}

function send(useBeacon) {
  if (!buffer.length) return;
  const events = buffer;
  buffer = [];
  try {
    const payload = JSON.stringify({ sid, page: pageId, upload_id: uploadId, ctx: context(), events });
    if (useBeacon && navigator.sendBeacon) {
      // 頁面卸載時只有 beacon 保證送得出去；用 text/plain 避免預檢請求
      if (navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'text/plain' }))) return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    // 日誌送不出去不能影響主功能
  }
}

export function clientLog(event, data = {}, level = 'info') {
  try {
    buffer.push({ seq: seq++, t: Date.now(), level, event, ...data });
    if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);
    clearTimeout(timer);
    if (level === 'error') send(false);
    else timer = setTimeout(() => send(false), FLUSH_DELAY_MS);
  } catch (e) {
    // 同上
  }
}

export const clientError = (event, data) => clientLog(event, data, 'error');

export function setLogUploadId(id) {
  uploadId = id || null;
}

export function getSessionId() {
  return sid;
}

export function flushClientLog() {
  clearTimeout(timer);
  send(true);
}

// 頁面卸載／切到背景：把還沒送的事件用 beacon 送出；回前景記一筆（判斷是否曾切背景）
window.addEventListener('pagehide', () => flushClientLog());
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushClientLog();
  else clientLog('visible');
});
window.addEventListener('online', () => clientLog('online'));
window.addEventListener('offline', () => clientLog('offline'));

// 沒被接住的 JS 錯誤：這是最常「畫面壞掉但沒人知道為什麼」的來源
window.addEventListener('error', e => {
  clientError('js_error', {
    message: String(e.message || e),
    src: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : undefined,
  });
});
window.addEventListener('unhandledrejection', e => {
  const r = e.reason;
  clientError('unhandled_rejection', { message: String((r && (r.message || r)) || 'unknown') });
});
